import { useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../../backend/convex/_generated/api';
import { Id } from '../../../../backend/convex/_generated/dataModel';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Icons } from '@/components/admin/Icons';
import { useAuth } from '@/context/use-auth';
import type { AdminUser, UserDetails } from '@/types';

export function AdminUsers() {
  const { user } = useAuth();
  const users = useQuery(
    api.admin.listUsers,
    user ? { clerkId: user.id } : 'skip'
  ) as AdminUser[] | undefined;
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<Id<'clerkUserProfiles'> | null>(null);

  const userDetails = useQuery(
    api.admin.getUserDetails,
    selectedUser && user ? { clerkProfileId: selectedUser, clerkId: user.id } : 'skip'
  ) as UserDetails | null | undefined;

  const filteredUsers = users?.filter((entry) => 
    entry.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <header className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-stone-800 mb-2">Users</h1>
            <p className="text-stone-500">Manage your readers</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-semibold text-stone-800">{users?.length || 0}</p>
            <p className="text-sm text-stone-500">total users</p>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Users List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
              <div className="p-4 border-b border-stone-200">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 pl-10 bg-stone-50 border border-stone-200 rounded-lg text-stone-800 placeholder-stone-400 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
                  />
                  <div className="absolute left-3 top-2.5 text-stone-400">
                    <Icons.Search />
                  </div>
                </div>
              </div>

              <div className="divide-y divide-stone-100">
                {!users ? (
                  <div className="p-6 space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-16 bg-stone-100 rounded animate-pulse" />
                    ))}
                  </div>
                ) : filteredUsers?.length === 0 ? (
                  <div className="px-6 py-12 text-center text-stone-400">
                    No users found
                  </div>
                ) : (
                  filteredUsers?.map((entry) => {
                    return (
                      <button
                        key={entry.id}
                        onClick={() => setSelectedUser(entry.id)}
                        className={`w-full px-6 py-4 flex items-center gap-4 text-left transition-colors hover:bg-stone-50 ${
                          selectedUser === entry.id ? 'bg-stone-50' : ''
                        }`}
                      >
                        <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-600 font-medium">
                          {entry.image ? (
                            <img src={entry.image} alt="" className="w-full h-full rounded-full object-cover" />
                          ) : (
                            entry.name?.[0]?.toUpperCase() || entry.email?.[0]?.toUpperCase() || '?'
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-stone-800 truncate">
                            {entry.name || 'Anonymous'}
                          </p>
                          <p className="text-sm text-stone-500 truncate">{entry.email}</p>
                        </div>

                        <div className="text-right">
                          <p className="text-sm font-medium text-teal-600">
                            {entry.tokenBalance} coins
                          </p>
                          <p className="text-xs text-stone-400">
                            {entry.tokensSpent > 0 && <span className="text-red-500">-{entry.tokensSpent} spent</span>}
                          </p>
                          <p className="text-xs text-stone-400">
                            {formatDate(entry.createdAt)}
                          </p>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* User Details Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-stone-200 overflow-hidden sticky top-6">
              {!selectedUser ? (
                <div className="px-6 py-12 text-center text-stone-400">
                  <div className="w-12 h-12 mx-auto mb-4">
                    <Icons.Users />
                  </div>
                  <p>Select a user to view details</p>
                </div>
              ) : !userDetails ? (
                <div className="p-6 space-y-4 animate-pulse">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-stone-100"></div>
                    <div className="flex-1">
                      <div className="h-5 w-32 bg-stone-100 rounded mb-2"></div>
                      <div className="h-4 w-48 bg-stone-100 rounded"></div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="p-6 border-b border-stone-200">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center text-2xl text-stone-600 font-medium">
                        {userDetails.user.image ? (
                          <img 
                            src={userDetails.user.image} 
                            alt="" 
                            className="w-full h-full rounded-full object-cover" 
                          />
                        ) : (
                          userDetails.user.name?.[0]?.toUpperCase() || 
                          userDetails.user.email?.[0]?.toUpperCase() || 
                          '?'
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-stone-800 truncate">
                          {userDetails.user.name || 'Anonymous'}
                        </h3>
                        <p className="text-sm text-stone-500 truncate">{userDetails.user.email}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-stone-50 rounded-lg p-3">
                        <p className="text-xs text-stone-500 mb-1">Coin Balance</p>
                        <p className="text-xl font-semibold text-teal-600">
                          {userDetails.profile?.tokenBalance || 0}
                        </p>
                      </div>

                      <div className="bg-stone-50 rounded-lg p-3">
                        <p className="text-xs text-stone-500 mb-1">Tokens Spent</p>
                        <p className="text-xl font-semibold text-red-600">
                          {userDetails.summary?.tokensSpent || 0}
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-3 bg-stone-50 rounded-lg p-3">
                      <p className="text-xs text-stone-500 mb-1">Total Transactions</p>
                      <p className="text-xl font-semibold text-stone-800">
                        {userDetails.summary?.totalTransactions || 0}
                      </p>
                    </div>
                  </div>

                  <div className="p-6">
                    <h4 className="text-sm font-medium text-stone-600 mb-4">Recent Transactions</h4>
                    
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {userDetails.transactions.slice(0, 10).map((tx) => (
                        <div key={tx._id} className="flex items-start justify-between text-sm p-3 bg-stone-50 rounded-lg">
                          <div className="flex flex-col flex-1 min-w-0">
                            <span className="capitalize font-medium text-stone-800">
                              {tx.type === 'spend' ? 'Chapter Unlock' : 
                               tx.type.replace('_', ' ')}
                            </span>
                            
                            {/* Chapter unlock details */}
                            {tx.type === 'spend' && tx.metadata && (
                              <div className="mt-1 space-y-0.5">
                                {tx.metadata.bookTitle && (
                                  <span className="text-xs text-stone-500 block truncate">
                                    {tx.metadata.bookTitle}
                                  </span>
                                )}
                                {tx.metadata.chapterTitle && (
                                  <span className="text-xs text-stone-400 block">
                                    {tx.metadata.chapterTitle}
                                  </span>
                                )}
                                <span className="text-xs text-stone-400">
                                  {tx.metadata.initialBalance} → {tx.metadata.finalBalance} coins
                                </span>
                              </div>
                            )}
                            
                            {/* Purchase details */}
                            {tx.type === 'purchase' && tx.metadata?.productName && (
                              <span className="text-xs text-stone-500 mt-1">{tx.metadata.productName}</span>
                            )}
                            
                            {/* Timestamp */}
                            <span className="text-xs text-stone-400 mt-1">
                              {new Date(tx.createdAt).toLocaleString()}
                            </span>
                          </div>
                          
                          <div className="text-right ml-3">
                            <span className={`font-bold text-base ${
                              tx.amount > 0 ? 'text-teal-600' : 'text-red-600'
                            }`}>
                              {tx.amount > 0 ? '+' : ''}{tx.amount}
                            </span>
                            {tx.metadata?.price && (
                              <span className="text-xs text-stone-400 block mt-1">${tx.metadata.price}</span>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {userDetails.transactions.length === 0 && (
                        <p className="text-sm text-stone-400 text-center py-4">No transactions yet</p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
